import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen";
import { RootPending } from "./components/page-state";
import { watchAccount } from "./lib/accountScope";
import { isLocalMode } from "./lib/appMode";
import "@fontsource-variable/bricolage-grotesque/opsz.css";
import "@fontsource-variable/instrument-sans";
// Player chrome type: Emby's stack is the Apple system font first, then its
// bundled Inter Variable everywhere else (Windows/Linux Chrome skips
// ui-sans-serif) — the wght axis is all the OSD needs.
import "@fontsource-variable/inter";
// Subtitle face — all three weights + italics so VTT <i>/<b> cues get real
// faces instead of synthetic slant/embolden.
import "@fontsource/noto-sans/400.css";
import "@fontsource/noto-sans/400-italic.css";
import "@fontsource/noto-sans/500.css";
import "@fontsource/noto-sans/500-italic.css";
import "@fontsource/noto-sans/700.css";
import "@fontsource/noto-sans/700-italic.css";
import "./styles/app.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});
// No key in this cache names the user: it is emptied whenever the account
// ends or changes (lib/accountScope).
watchAccount(queryClient);

const router = createRouter({
  routeTree,
  // The server-embedded copy lives under /web/ (rago-server's mount); the
  // router strips it from its own location.pathname, so routes and links
  // are written base-free in both builds.
  basepath: isLocalMode ? "/web" : undefined,
  defaultPreload: "intent",
  scrollRestoration: true,
  // The root route awaits auth.bootstrap() (refresh cookie → tokens) before
  // anything renders. A slow one used to be a black screen; past 300 ms it
  // is the same amber spinner Home continues with once the bar fades in.
  defaultPendingComponent: RootPending,
  defaultPendingMs: 300,
  defaultPendingMinMs: 300,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
